import { Injectable } from '@angular/core';
import { AbstractSecurityStorage } from 'angular-auth-oidc-client';

/**
 * The OIDC client's storage on /embed/ pages: in memory, so tokens handed over by the
 * framing page never reach the (partitioned) browser storage and leave with the frame.
 */
@Injectable({ providedIn: 'root' })
export class EmbedSecurityStorage implements AbstractSecurityStorage {
  private readonly _items = new Map<string, string>();

  read(key: string): string | null {
    return this._items.get(key) ?? null;
  }

  write(key: string, value: string): void {
    this._items.set(key, value);
  }

  remove(key: string): void {
    this._items.delete(key);
  }

  clear(): void {
    this._items.clear();
  }

  /**
   * Stores tokens as the OIDC client does after a code exchange (angular-auth-oidc-client
   * 16 keeps one JSON record per config), so its interceptor, user data and checkAuth
   * work unchanged. The user data is the ID token's claims, as with `autoUserInfo: false`.
   */
  setTokens(
    configId: string,
    tokens: {
      accessToken: string;
      idToken: string;
      expiresAt: number;
      userData: unknown;
    }
  ): void {
    const record = JSON.parse(this.read(configId) ?? '{}') as Record<
      string,
      unknown
    >;
    this.write(
      configId,
      JSON.stringify({
        ...record,
        authzData: tokens.accessToken,
        authnResult: {
          access_token: tokens.accessToken,
          id_token: tokens.idToken,
        },
        access_token_expires_at: tokens.expiresAt,
        userData: tokens.userData,
      })
    );
  }
}
